// @ts-nocheck
/* eslint-disable @typescript-eslint/no-explicit-any */
// Location: src/components/admin/Archive.tsx
// Purpose: Archive page for viewing published content and comments
// Scope: Shows published content and comments in a searchable and filterable interface

import * as React from 'react';
import { Card, CardTitle, CardContent } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { PageLoader } from '@/components/ui/loader';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Id } from '@/convex/_generated/dataModel';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { CommentFeedbackModal } from './CommentFeedbackModal';
import { toast } from '@/components/ui/use-toast';

interface ContentHistoryItem {
	_id: Id<'contentHistory'>;
	content: string;
	status: string;
	createdAt: string;
	platform?: string;
	profileId?: Id<'profiles'>; // The social profile that published this content
	metadata?: {
		buzzUrl?: string;
		templateName?: string;
		postUrl?: string;
		postId?: string;
		conversationId?: string;
		publishedAt?: string;
		swarmPodId?: Id<'engagementPods'>;
		platform?: string;
		engagement?: {
			likes?: number;
			comments?: number;
			shares?: number;
			views?: number;
		};
	};
	brandVoice?: {
		id: string;
		profileId?: Id<'profiles'>;
	};
}

interface TwitterComment {
	_id: Id<'twitterXComments'>;
	conversationId: string;
	status: string;
	aiReply?: string;
	twitterXPostId: Id<'twitterXPosts'>;
	podId: Id<'engagementPods'>;
	platform: string;
	socialUsername: string;
	commentAuthor: string;
	postUrl: string;
	postText: string;
	postImageUrls: string[];
	imageAnalysis?: string;
	replyDate?: string;
	scheduleId?: Id<'schedules'>;
	createdAt: string;
	lastModified: string;
	publishedAt?: string;
	aiCommentUrl?: string;
	feedbackStatus?: 'approved' | 'needs_edit' | 'rejected';
	userEditedComment?: string;
	feedbackTimestamp?: string;
	feedbackUserId?: Id<'users'>;
}

const ContentCard = ({ item }: { item: ContentHistoryItem }) => {
	// Get the profile to find the actual user who published this content
	const profile = useQuery(
		api.profiles.getProfile,
		item.profileId ? { profileId: item.profileId } : 'skip'
	);

	const getFirstSentence = (text: string) => {
		const match = text.match(/^[^.!?\n]+[.!?\n]/);
		if (match) {
			const sentence = match[0].trim();
			return sentence.length > 100
				? sentence.substring(0, 97) + '...'
				: sentence;
		}
		return text.length > 100 ? text.substring(0, 97) + '...' : text;
	};

	return (
		<Card className='p-6 hover:shadow-lg transition-shadow duration-200'>
			<div className='space-y-4'>
				<div className='flex flex-col gap-4'>
					<div className='flex items-center gap-2 flex-wrap'>
						<Badge
							variant='outline'
							className='bg-gradient-to-b from-pink-400 to-pink-600 text-white px-3 py-1 whitespace-nowrap'
						>
							🚀 Published
						</Badge>
						{item.brandVoice?.id && (
							<Badge
								variant='outline'
								className='bg-gradient-to-b from-purple-400 to-purple-600 text-white px-3 py-1 whitespace-nowrap'
							>
								{item.brandVoice.id === 'prometheus' &&
									'🔥 Prometheus'}
								{item.brandVoice.id === 'reverend' &&
									'🎭 The Reverend'}
								{item.brandVoice.id === 'brent' &&
									'💼 Brent Ecomm Master'}
								{item.brandVoice.id === 'user' &&
									'✨ My Brand Voice'}
							</Badge>
						)}
						{item.metadata?.templateName ? (
							<Badge
								variant='outline'
								className='bg-gradient-to-b from-blue-400 to-blue-600 text-white px-3 py-1 whitespace-nowrap'
							>
								📝 {item.metadata.templateName}
							</Badge>
						) : (
							<Badge
								variant='outline'
								className='bg-gradient-to-b from-gray-400 to-gray-600 text-white px-3 py-1 whitespace-nowrap'
							>
								🎯 No Template
							</Badge>
						)}
					</div>
					<h3 className='text-xl font-bold bg-gradient-to-b from-pink-400 to-pink-600 text-transparent bg-clip-text leading-tight'>
						{getFirstSentence(item.content)}
					</h3>
				</div>

				<div className='text-base leading-relaxed text-gray-700 dark:text-gray-300'>
					{item.content.length > 200
						? item.content.substring(0, 200) + '...'
						: item.content}
				</div>

				<div className='flex flex-col gap-2 text-sm'>
					<div className='text-gray-600 dark:text-blue-400'>
						<span className='font-bold text-gray-900 dark:text-gray-200'>
							Published:{' '}
						</span>
						{item.metadata?.publishedAt
							? new Date(
									item.metadata.publishedAt
								).toLocaleString()
							: new Date(item.createdAt).toLocaleString()}
					</div>
					<div className='text-gray-600 dark:text-blue-400'>
						<span className='font-bold text-gray-900 dark:text-gray-200'>
							Published By:{' '}
						</span>
						{profile?.profileTitle || 'Unknown User'}
					</div>
					{item.metadata?.postUrl && (
						<div className='text-gray-600 dark:text-blue-400'>
							<span className='font-bold text-gray-900 dark:text-gray-200'>
								URL:{' '}
							</span>
							<a
								href={item.metadata.postUrl}
								target='_blank'
								rel='noopener noreferrer'
								className='hover:text-blue-600 dark:hover:text-blue-300 underline'
							>
								View Post
							</a>
						</div>
					)}
				</div>
			</div>
		</Card>
	);
};

const CommentCard = ({ comment }: { comment: TwitterComment }) => {
	const { user } = useAuth();
	const [feedbackModalOpen, setFeedbackModalOpen] = React.useState(false);
	const approveCommentMutation = useMutation(
		api.twitterXComments.approveComment
	);

	// Get the profile who published this comment
	const profile = useQuery(
		api.profiles.getProfileByEmail,
		comment.commentAuthor ? { email: comment.commentAuthor } : 'skip'
	);

	const handleApprove = async () => {
		if (!user?.id) {
			toast({
				title: 'Error',
				description: 'You must be logged in to provide feedback',
				variant: 'destructive',
			});
			return;
		}

		try {
			await approveCommentMutation({
				commentId: comment._id,
				userId: user.id as unknown as Id<'users'>,
			});

			toast({
				title: '✅ Comment Approved',
				description: 'This comment has been marked as approved',
			});
		} catch (error) {
			console.error('Failed to approve comment:', error);
			toast({
				title: 'Error',
				description: 'Failed to approve comment',
				variant: 'destructive',
			});
		}
	};

	const handleReject = () => {
		setFeedbackModalOpen(true);
	};

	return (
		<Card className='p-6 hover:shadow-lg transition-shadow duration-200'>
			<div className='space-y-4'>
				<div className='flex flex-col gap-4'>
					<div className='flex items-center gap-2 flex-wrap'>
						<Badge
							variant='outline'
							className='bg-gradient-to-b from-purple-400 to-pink-600 text-white px-3 py-1 whitespace-nowrap'
						>
							𝕏{' '}
							{comment.platform === 'twitterx'
								? 'twitterX'
								: comment.platform}
						</Badge>
						<Badge
							variant='outline'
							className={cn(
								'px-3 py-1 whitespace-nowrap',
								comment.status === '✅ Comment Published'
									? 'bg-gradient-to-b from-emerald-400 to-green-600 text-white'
									: 'bg-gradient-to-b from-yellow-400 to-yellow-600 text-white'
							)}
						>
							{comment.status === '✅ Comment Published'
								? '✅ Published'
								: comment.status}
						</Badge>
					</div>
					<h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
						Comment on{' '}
						{comment.platform === 'twitterx'
							? '𝕏'
							: comment.platform}
					</h3>
				</div>

				<div className='text-base leading-relaxed text-gray-700 dark:text-gray-300'>
					{comment.aiReply && comment.aiReply.length > 200
						? comment.aiReply.substring(0, 200) + '...'
						: comment.aiReply || 'No AI reply available'}
				</div>

				<div className='flex flex-col gap-2 text-sm'>
					<div className='text-gray-600 dark:text-blue-400'>
						<span className='font-bold text-gray-900 dark:text-gray-200'>
							Published:{' '}
						</span>
						{comment.publishedAt
							? new Date(comment.publishedAt).toLocaleString()
							: new Date(comment.createdAt).toLocaleString()}
					</div>
					<div className='text-gray-600 dark:text-blue-400'>
						<span className='font-bold text-gray-900 dark:text-gray-200'>
							Published By:{' '}
						</span>
						{profile?.profileTitle ||
							comment.commentAuthor ||
							'Unknown User'}
					</div>
					{comment.aiCommentUrl && (
						<div className='text-gray-600 dark:text-blue-400'>
							<span className='font-bold text-gray-900 dark:text-gray-200'>
								URL:{' '}
							</span>
							<a
								href={comment.aiCommentUrl}
								target='_blank'
								rel='noopener noreferrer'
								className='hover:text-blue-600 dark:hover:text-blue-300 underline'
							>
								View Comment
							</a>
						</div>
					)}
					<div className='text-gray-600 dark:text-blue-400'>
						<span className='font-bold text-gray-900 dark:text-gray-200'>
							Original Post:{' '}
						</span>
						{comment.postUrl &&
						comment.postUrl.startsWith('http') ? (
							<a
								href={comment.postUrl}
								target='_blank'
								rel='noopener noreferrer'
								className='hover:text-blue-600 dark:hover:text-blue-300 underline'
							>
								View Original
							</a>
						) : (
							<span className='text-gray-500 italic'>
								URL not available
							</span>
						)}
					</div>
				</div>

				{/* Feedback Buttons */}
				{comment.aiReply && (
					<div className='flex items-center gap-2 pt-4 border-t border-gray-200 dark:border-gray-700'>
						<div className='flex gap-2'>
							<Button
								variant='outline'
								size='sm'
								onClick={handleApprove}
								className={cn(
									'flex items-center gap-1 transition-all',
									comment.feedbackStatus === 'approved'
										? 'bg-green-500/20 border-green-500 text-green-500 hover:bg-green-500/30'
										: 'hover:bg-green-500/10 hover:border-green-500 hover:text-green-500'
								)}
								disabled={comment.feedbackStatus === 'approved'}
							>
								<ThumbsUp className='h-4 w-4' />
								{comment.feedbackStatus === 'approved'
									? 'Approved'
									: 'Approve'}
							</Button>
							<Button
								variant='outline'
								size='sm'
								onClick={handleReject}
								className={cn(
									'flex items-center gap-1 transition-all',
									comment.feedbackStatus === 'needs_edit'
										? 'bg-orange-500/20 border-orange-500 text-orange-500 hover:bg-orange-500/30'
										: 'hover:bg-orange-500/10 hover:border-orange-500 hover:text-orange-500'
								)}
							>
								<ThumbsDown className='h-4 w-4' />
								{comment.feedbackStatus === 'needs_edit'
									? 'Edited'
									: 'Edit'}
							</Button>
						</div>
						{comment.feedbackStatus && (
							<Badge
								variant='outline'
								className={cn(
									'ml-auto',
									comment.feedbackStatus === 'approved'
										? 'bg-green-500/20 text-green-500'
										: comment.feedbackStatus ===
											  'needs_edit'
											? 'bg-orange-500/20 text-orange-500'
											: 'bg-red-500/20 text-red-500'
								)}
							>
								{comment.feedbackStatus === 'approved' &&
									'✅ AI Approved'}
								{comment.feedbackStatus === 'needs_edit' &&
									'✏️ User Edited'}
								{comment.feedbackStatus === 'rejected' &&
									'❌ Rejected'}
							</Badge>
						)}
					</div>
				)}
			</div>

			{/* Feedback Modal */}
			<CommentFeedbackModal
				isOpen={feedbackModalOpen}
				onClose={() => setFeedbackModalOpen(false)}
				commentId={comment._id}
				originalComment={comment.aiReply || ''}
				postText={comment.postText}
				socialUsername={comment.socialUsername}
				onSuccess={() => {
					toast({
						title: '✅ Feedback Saved',
						description:
							'Your feedback will help improve future comments',
					});
				}}
			/>
		</Card>
	);
};

export function Archive() {
	const { user, isAdmin, isLoading: authLoading } = useAuth();
	const [isPageLoading, setIsPageLoading] = React.useState(true);
	const [activeTab, setActiveTab] = React.useState('content');
	const [contentFilter, setContentFilter] = React.useState<
		'user' | 'community'
	>('user');
	const [platformFilter, setPlatformFilter] = React.useState<
		'all' | 'twitterx' | 'linkedin'
	>('all');
	const [searchTerm, setSearchTerm] = React.useState('');
	const [dateSearch, setDateSearch] = React.useState('');
	const [currentContentPage, setCurrentContentPage] = React.useState(1);
	const [currentCommentsPage, setCurrentCommentsPage] = React.useState(1);
	const itemsPerPage = 6;

	// Fetch content based on filter
	const contentHistory = useQuery(
		api.contentMachine.getAllPublishedContent,
		contentFilter === 'user' && user?.id
			? { userId: user.id as unknown as Id<'users'> }
			: {}
	);
	// console.log(
	// 	'[ARCHIVE_DEBUG] Content history query result:',
	// 	contentHistory?.length
	// );

	// Fetch comments based on filter using COUNTER SYSTEM (not massive data queries)
	const userComments = useQuery(
		api.twitterXComments.getPublishedCommentsForUser,
		contentFilter === 'user' && user?.id
			? { userId: user?.id as unknown as Id<'users'>, limit: 500 } // Reduced limit for performance
			: 'skip'
	);
	// console.log(
	// 	'[ARCHIVE_DEBUG] User comments query result:',
	// 	userComments?.length
	// );

	const communityComments = useQuery(
		api.twitterXComments.getAllPublishedComments,
		contentFilter === 'community' && user?.id
			? { excludeUserId: user.id as unknown as Id<'users'>, limit: 500 } // Add limit for performance
			: 'skip'
	);
	// console.log(
	// 	'[ARCHIVE_DEBUG] Community comments query result:',
	// 	communityComments?.length
	// );

	// Get total counts using counters (not loading all data)
	const userCommentsTotal = useQuery(
		api.twitterXComments.getTotalPublishedCommentsCount,
		contentFilter === 'user' && user?.id
			? { userId: user?.id as unknown as Id<'users'> }
			: 'skip'
	);

	const communityCommentsTotal = useQuery(
		api.twitterXComments.getTotalAllPublishedCommentsCount,
		contentFilter === 'community' && user?.id
			? { excludeUserId: user.id as unknown as Id<'users'> }
			: 'skip'
	);

	const twitterCommentsResult =
		contentFilter === 'user' ? userComments : communityComments;

	React.useEffect(() => {
		if (!authLoading) {
			setIsPageLoading(false);
		}
	}, [authLoading]);

	// Debug logging - moved before early returns to fix hooks order
	// React.useEffect(() => {
	// 	console.log(
	// 		'[ARCHIVE_DEBUG] Raw content history count:',
	// 		contentHistory?.length
	// 	);
	// 	console.log(
	// 		'[ARCHIVE_DEBUG] Raw comments count:',
	// 		twitterCommentsResult?.length
	// 	);
	// }, [contentHistory, twitterCommentsResult]);

	if (isPageLoading) {
		return <PageLoader message='LOADING ARCHIVE...' />;
	}

	if (!user || !isAdmin) {
		return (
			<div className='container mx-auto p-6'>
				<Card className='mt-6'>
					<CardContent>
						<div className='flex flex-col items-center justify-center space-y-6 p-8'>
							<h2 className='text-2xl font-bold text-red-500'>
								Access Denied
							</h2>
							<p className='text-lg text-gray-400 text-center'>
								You do not have permission to access the
								Archive.
							</p>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	// Filter content (already filtered by user/community in query)
	const filteredContent =
		contentHistory
			?.filter((item: ContentHistoryItem) => {
				// Log item for debugging
				// console.log('[ARCHIVE_DEBUG] Content item:', {
				// 	id: item._id,
				// 	hasPublishedAt: !!item.metadata?.publishedAt,
				// 	hasPostUrl: !!item.metadata?.postUrl,
				// 	status: item.status,
				// 	publishedAt: item.metadata?.publishedAt,
				// 	postUrl: item.metadata?.postUrl,
				// });

				// Only show published content
				const isPublished =
					item.metadata?.publishedAt ||
					item.metadata?.postUrl ||
					item.status === 'published';
				if (!isPublished) {
					// console.log(
					// 	'[ARCHIVE_DEBUG] Content filtered out (not published):',
					// 	item._id
					// );
					return false;
				}

				const matchesSearch =
					searchTerm === '' ||
					item.content
						.toLowerCase()
						.includes(searchTerm.toLowerCase());

				// Date filtering - only check publishedAt for published content
				const matchesDate =
					dateSearch === '' ||
					(item.metadata?.publishedAt &&
						format(
							new Date(item.metadata.publishedAt),
							'yyyy-MM-dd'
						) === dateSearch);

				// Platform filtering for content
				const itemPlatform =
					item.platform || item.metadata?.platform || '';
				const matchesPlatform =
					platformFilter === 'all' ||
					itemPlatform.toLowerCase().includes(platformFilter) ||
					(platformFilter === 'twitterx' &&
						(itemPlatform.toLowerCase().includes('twitter') ||
							itemPlatform.toLowerCase().includes('x'))) ||
					(platformFilter === 'linkedin' &&
						itemPlatform.toLowerCase().includes('linkedin'));

				const result = matchesSearch && matchesDate && matchesPlatform;
				if (!result) {
					// console.log(
					// 	'[ARCHIVE_DEBUG] Content filtered out by filters:',
					// 	{
					// 		id: item._id,
					// 		matchesSearch,
					// 		matchesDate,
					// 		matchesPlatform,
					// 	}
					// );
				}
				return result;
			})
			?.sort(
				(a: ContentHistoryItem, b: ContentHistoryItem) =>
					new Date(b.metadata?.publishedAt || b.createdAt).getTime() -
					new Date(a.metadata?.publishedAt || a.createdAt).getTime()
			) || [];

	// Log filtered content count
	// console.log(
	// 	'[ARCHIVE_DEBUG] Filtered content count:',
	// 	filteredContent.length
	// );

	// Filter comments (already filtered by user/community in query)
	const filteredComments =
		twitterCommentsResult
			?.filter((comment: any) => {
				// Log comment for debugging
				// console.log('[ARCHIVE_DEBUG] Comment item:', {
				// 	id: comment._id,
				// 	status: comment.status,
				// 	hasPublishedAt: !!comment.publishedAt,
				// 	hasAiCommentUrl: !!comment.aiCommentUrl,
				// 	publishedAt: comment.publishedAt,
				// 	aiCommentUrl: comment.aiCommentUrl,
				// });

				// Only show published comments - check for both old and new status formats
				const isPublished =
					comment.status === '✅ Comment Published' ||
					comment.status === '✅ Published' || // New format after refactor
					(comment.publishedAt && comment.publishedAt !== '');
				if (!isPublished) {
					// console.log(
					// 	'[ARCHIVE_DEBUG] Comment filtered out (not published):',
					// 	comment._id,
					// 	'status:',
					// 	comment.status,
					// 	'hasPublishedAt:',
					// 	!!comment.publishedAt,
					// 	'hasAiCommentUrl:',
					// 	!!comment.aiCommentUrl
					// );
					return false;
				}

				const matchesSearch =
					searchTerm === '' ||
					(comment.aiReply &&
						comment.aiReply
							.toLowerCase()
							.includes(searchTerm.toLowerCase())) ||
					comment.socialUsername
						.toLowerCase()
						.includes(searchTerm.toLowerCase());

				// Date filtering for comments - only check publishedAt
				const matchesDate =
					dateSearch === '' ||
					(comment.publishedAt &&
						format(new Date(comment.publishedAt), 'yyyy-MM-dd') ===
							dateSearch);

				// Platform filtering for comments - handle case variations and naming inconsistencies
				const commentPlatform = comment.platform || '';
				const matchesPlatform =
					platformFilter === 'all' ||
					commentPlatform.toLowerCase().includes(platformFilter) ||
					(platformFilter === 'twitterx' &&
						(commentPlatform.toLowerCase().includes('twitter') ||
							commentPlatform.toLowerCase().includes('x'))) ||
					(platformFilter === 'linkedin' &&
						commentPlatform.toLowerCase().includes('linkedin'));

				const result = matchesSearch && matchesDate && matchesPlatform;
				if (!result) {
					// console.log(
					// 	'[ARCHIVE_DEBUG] Comment filtered out by filters:',
					// 	{
					// 		id: comment._id,
					// 		matchesSearch,
					// 		matchesDate,
					// 		matchesPlatform,
					// 	}
					// );
				}
				return result;
			})
			?.sort(
				(a: any, b: any) =>
					new Date(b.publishedAt || b.createdAt).getTime() -
					new Date(a.publishedAt || a.createdAt).getTime()
			) || [];

	// Log filtered comments count
	// console.log(
	// 	'[ARCHIVE_DEBUG] Filtered comments count:',
	// 	filteredComments.length
	// );

	// Pagination for content
	const totalContentPages = Math.ceil(filteredContent.length / itemsPerPage);
	const currentContentItems = filteredContent.slice(
		(currentContentPage - 1) * itemsPerPage,
		currentContentPage * itemsPerPage
	);

	// Pagination for comments
	const totalCommentsPages = Math.ceil(
		filteredComments.length / itemsPerPage
	);
	const currentCommentsItems = filteredComments.slice(
		(currentCommentsPage - 1) * itemsPerPage,
		currentCommentsPage * itemsPerPage
	);

	const renderPagination = (
		currentPage: number,
		totalPages: number,
		setCurrentPage: (page: number) => void
	) => {
		if (totalPages <= 1) return null;

		return (
			<div className='flex flex-col items-center justify-center gap-4 mt-8'>
				<div className='flex items-center gap-4'>
					<Button
						variant='outline'
						size='sm'
						onClick={() => setCurrentPage(1)}
						disabled={currentPage === 1}
						className='bg-gradient-to-b from-purple-400 to-purple-600 hover:from-purple-500 hover:to-purple-700 text-white'
					>
						⏮️ First
					</Button>
					<Button
						variant='outline'
						size='sm'
						onClick={() => setCurrentPage(currentPage - 1)}
						disabled={currentPage === 1}
						className='bg-gradient-to-b from-purple-400 to-purple-600 hover:from-purple-500 hover:to-purple-700 text-white'
					>
						← Previous
					</Button>
					<div className='flex items-center gap-2'>
						{Array.from(
							{ length: Math.min(totalPages, 5) },
							(_, i) => {
								const pageNum = i + 1;
								return (
									<Button
										key={pageNum}
										variant='outline'
										size='sm'
										onClick={() => setCurrentPage(pageNum)}
										className={cn(
											'w-10 h-10',
											currentPage === pageNum
												? 'bg-gradient-to-b from-pink-400 to-pink-600 hover:from-pink-500 hover:to-pink-700 text-white'
												: 'bg-gradient-to-b from-gray-400 to-gray-600 hover:from-gray-500 hover:to-gray-700 text-white'
										)}
									>
										{pageNum}
									</Button>
								);
							}
						)}
					</div>
					<Button
						variant='outline'
						size='sm'
						onClick={() => setCurrentPage(currentPage + 1)}
						disabled={currentPage === totalPages}
						className='bg-gradient-to-b from-purple-400 to-purple-600 hover:from-purple-500 hover:to-purple-700 text-white'
					>
						Next →
					</Button>
					<Button
						variant='outline'
						size='sm'
						onClick={() => setCurrentPage(totalPages)}
						disabled={currentPage === totalPages}
						className='bg-gradient-to-b from-purple-400 to-purple-600 hover:from-purple-500 hover:to-purple-700 text-white'
					>
						Last ⏭️
					</Button>
				</div>
				<div className='text-sm text-gray-400 text-center'>
					<p>
						Showing items{' '}
						{currentPage * itemsPerPage - itemsPerPage + 1} to{' '}
						{Math.min(
							currentPage * itemsPerPage,
							activeTab === 'content'
								? filteredContent.length
								: filteredComments.length
						)}{' '}
						of{' '}
						{activeTab === 'content'
							? filteredContent.length
							: filteredComments.length}{' '}
						total items
					</p>
				</div>
			</div>
		);
	};

	return (
		<div className='container mx-auto p-6'>
			<Card className='mt-6'>
				<CardContent className='pt-6'>
					<div className='space-y-3 mb-8'>
						<CardTitle className='text-4xl font-bold bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 text-transparent bg-clip-text'>
							Archive
						</CardTitle>
						<p className='text-sm text-muted-foreground font-normal'>
							Published content & engagement
						</p>
					</div>

					{/* Filter Pills */}
					<div className='flex flex-col gap-4 mb-6'>
						{/* Content Type Filter */}
						<div className='flex items-center gap-4'>
							<div className='flex items-center gap-2'>
								<span className='text-sm font-medium text-gray-600 dark:text-gray-400'>
									Show:
								</span>
								<div className='flex gap-2'>
									<Button
										variant='outline'
										size='sm'
										onClick={() => {
											setContentFilter('user');
											setCurrentContentPage(1);
											setCurrentCommentsPage(1);
										}}
										className={cn(
											'px-4 py-2 rounded-full font-semibold transition-all duration-300',
											contentFilter === 'user'
												? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg border-transparent'
												: 'bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 text-gray-700 dark:text-gray-300 hover:from-blue-400 hover:to-purple-500 hover:text-white'
										)}
									>
										✨ My Content
									</Button>
									<Button
										variant='outline'
										size='sm'
										onClick={() => {
											setContentFilter('community');
											setCurrentContentPage(1);
											setCurrentCommentsPage(1);
										}}
										className={cn(
											'px-4 py-2 rounded-full font-semibold transition-all duration-300',
											contentFilter === 'community'
												? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg border-transparent'
												: 'bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 text-gray-700 dark:text-gray-300 hover:from-emerald-400 hover:to-teal-500 hover:text-white'
										)}
									>
										🌍 Community
									</Button>
								</div>
							</div>
						</div>

						{/* Platform Filter (visible on both tabs) */}
						{(activeTab === 'comments' ||
							activeTab === 'content') && (
							<div className='flex items-center gap-4'>
								<div className='flex items-center gap-2'>
									<span className='text-sm font-medium text-gray-600 dark:text-gray-400'>
										Platform:
									</span>
									<div className='flex gap-2'>
										<Button
											variant='outline'
											size='sm'
											onClick={() => {
												setPlatformFilter('all');
												setCurrentCommentsPage(1);
											}}
											className={cn(
												'px-4 py-2 rounded-full font-semibold transition-all duration-300',
												platformFilter === 'all'
													? 'bg-gradient-to-r from-gray-500 to-gray-700 text-white shadow-lg border-transparent'
													: 'bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 text-gray-700 dark:text-gray-300 hover:from-gray-400 hover:to-gray-600 hover:text-white'
											)}
										>
											🌐 All Platforms
										</Button>
										<Button
											variant='outline'
											size='sm'
											onClick={() => {
												setPlatformFilter('twitterx');
												setCurrentCommentsPage(1);
											}}
											className={cn(
												'px-4 py-2 rounded-full font-semibold transition-all duration-300',
												platformFilter === 'twitterx'
													? 'bg-gradient-to-r from-blue-400 to-blue-600 text-white shadow-lg border-transparent'
													: 'bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 text-gray-700 dark:text-gray-300 hover:from-blue-400 hover:to-blue-600 hover:text-white'
											)}
										>
											𝕏 Twitter
										</Button>
										<Button
											variant='outline'
											size='sm'
											onClick={() => {
												setPlatformFilter('linkedin');
												setCurrentCommentsPage(1);
											}}
											className={cn(
												'px-4 py-2 rounded-full font-semibold transition-all duration-300',
												platformFilter === 'linkedin'
													? 'bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-lg border-transparent'
													: 'bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 text-gray-700 dark:text-gray-300 hover:from-blue-600 hover:to-blue-800 hover:text-white'
											)}
										>
											💼 LinkedIn
										</Button>
									</div>
								</div>
							</div>
						)}
					</div>

					<Tabs
						value={activeTab}
						onValueChange={(tab) => {
							setActiveTab(tab);
							setPlatformFilter('all'); // Reset platform filter when switching tabs
							setCurrentContentPage(1);
							setCurrentCommentsPage(1);
						}}
						className='space-y-6'
					>
						<TabsList className='grid w-full grid-cols-2 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 p-1 rounded-full shadow-lg'>
							<TabsTrigger
								value='content'
								className='flex items-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-pink-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all duration-300 rounded-full font-semibold'
							>
								🔥 CONTENT ({filteredContent.length})
							</TabsTrigger>
							<TabsTrigger
								value='comments'
								className='flex items-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-pink-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all duration-300 rounded-full font-semibold'
							>
								⚡️ COMMENTS (
								{contentFilter === 'user'
									? (userCommentsTotal ?? 0)
									: (communityCommentsTotal ?? 0)}
								)
							</TabsTrigger>
						</TabsList>

						{/* Search and Filter Controls */}
						<div className='flex items-center gap-4 mb-6'>
							<Input
								placeholder='Search content...'
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className='max-w-xs'
							/>
							<Input
								type='date'
								value={dateSearch}
								onChange={(e) => setDateSearch(e.target.value)}
								className='max-w-xs'
							/>
							{(searchTerm ||
								dateSearch ||
								contentFilter === 'community' ||
								platformFilter !== 'all') && (
								<Button
									variant='outline'
									onClick={() => {
										setSearchTerm('');
										setDateSearch('');
										setContentFilter('user');
										setPlatformFilter('all');
										setCurrentContentPage(1);
										setCurrentCommentsPage(1);
									}}
									className='bg-gradient-to-b from-gray-400 to-gray-600 hover:from-gray-500 hover:to-gray-700 text-white'
								>
									Clear All Filters
								</Button>
							)}
						</div>

						<TabsContent value='content' className='space-y-6'>
							{currentContentItems.length > 0 ? (
								<>
									<div className='grid gap-6 md:grid-cols-2 lg:grid-cols-3'>
										{currentContentItems.map(
											(item: ContentHistoryItem) => (
												<ContentCard
													key={item._id}
													item={item}
												/>
											)
										)}
									</div>
									{renderPagination(
										currentContentPage,
										totalContentPages,
										setCurrentContentPage
									)}
								</>
							) : (
								<Card className='p-10'>
									<div className='flex flex-col items-center justify-center gap-6 py-12 text-center'>
										<div className='text-6xl animate-bounce'>
											📝
										</div>
										<h3 className='text-2xl font-bold bg-gradient-to-r from-pink-400 via-purple-400 to-blue-400 text-transparent bg-clip-text'>
											No published content found
										</h3>
										<p className='text-lg text-gray-600 dark:text-gray-400'>
											{searchTerm || dateSearch
												? 'Try adjusting your search criteria'
												: 'Start creating and publishing content to see it here!'}
										</p>
									</div>
								</Card>
							)}
						</TabsContent>

						<TabsContent value='comments' className='space-y-6'>
							{currentCommentsItems.length > 0 ? (
								<>
									<div className='grid gap-6 md:grid-cols-2 lg:grid-cols-3'>
										{currentCommentsItems.map(
											(comment: any) => (
												<CommentCard
													key={comment._id}
													comment={comment}
												/>
											)
										)}
									</div>
									{renderPagination(
										currentCommentsPage,
										totalCommentsPages,
										setCurrentCommentsPage
									)}
								</>
							) : (
								<Card className='p-10'>
									<div className='flex flex-col items-center justify-center gap-6 py-12 text-center'>
										<div className='text-6xl animate-bounce'>
											💬
										</div>
										<h3 className='text-2xl font-bold bg-gradient-to-r from-green-400 via-blue-400 to-purple-400 text-transparent bg-clip-text'>
											No published comments found
										</h3>
										<p className='text-lg text-gray-600 dark:text-gray-400'>
											{searchTerm || dateSearch
												? 'Try adjusting your search criteria'
												: 'Start engaging with your audience to see comments here!'}
										</p>
									</div>
								</Card>
							)}
						</TabsContent>
					</Tabs>
				</CardContent>
			</Card>
		</div>
	);
}
